import Spinner from "@components/ui/spinner";
import { cn } from "@lib/cn";
import { DialogBase } from "@modules/slides-library/shared";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { useCallback } from "react";
import { toast } from "react-toastify";
import Button from "@/components/ui/button";
import { handleAsyncOperation } from "@/shared";
import { useArchiveSlidesFolderMutation } from "@/shared/services";
import type { TSlidesFolder } from "@/shared/types";

const DIALOG_ID = "delete-folder-dialog";

const FolderDeleteDialog = ({ folder }: { folder: TSlidesFolder }) => {
  const dispatch = useAppDispatch();
  const { mutateAsync: archiveFolder, isPending: isLoading } =
    useArchiveSlidesFolderMutation();

  const handleClose = useCallback(
    () => dispatch(closeDialog(DIALOG_ID)),
    [dispatch],
  );

  const handleSubmit = useCallback(async () => {
    if (isLoading) return;

    await handleAsyncOperation({
      operation: async () => {
        await archiveFolder(folder._id);
        toast.success("Folder moved to recently deleted");
        handleClose();
      },
      onError: () => toast.error("Failed to move folder to recently deleted"),
    });
  }, [archiveFolder, folder._id, handleClose, isLoading]);

  return (
    <DialogBase>
      <DialogBase.Header title="Delete folder" onClose={handleClose} />
      <DialogBase.Content className="tw-pt-4">
        <p className="tw-m-0 tw-text-[13px] tw-font-normal tw-text-blue">
          Are you sure that you want to delete{" "}
          <span className="tw-font-semibold">“{folder.title}”</span> folder
          <br />
          with all of its subfolders and slides?
          <br />
          <br />
          You can restore it from{" "}
          <span className="tw-font-semibold">Recently deleted</span>.
        </p>
      </DialogBase.Content>
      <DialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isLoading}
          className={cn(isLoading && "tw-cursor-not-allowed")}
          variant="destructive"
        >
          {isLoading ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Deleting...
            </>
          ) : (
            "Yes, delete"
          )}
        </Button>
      </DialogBase.Footer>
    </DialogBase>
  );
};

FolderDeleteDialog.id = DIALOG_ID;
export { FolderDeleteDialog };
